import { Link } from 'react-router-dom';
import { CloudOff, RefreshCw } from 'lucide-react';
import { Button } from '@/components/Button';
import { deploymentAddressLabel, effectiveDeploymentAddress } from './deploymentAddress';
import { AUTH_CARD_CLASS, AuthFrame } from '@/components/AuthFrame';
import { isShellRuntime } from '@/shell/shellDetection';
import { WAITING_MOTION } from '@featit/shared/brand/waiting-motion';
import { Waiting } from '@/components/Waiting';
import { useT } from '@/i18n/LocaleProvider';
import { sayFailure, type HeldSentenceType } from '@/services/actionFailure';
import { registerEnglishArea } from '@/i18n/catalogue';
import { onboarding } from '@featit/shared/i18n/messages/en/onboarding';

registerEnglishArea('onboarding', onboarding);

/**
 * What the app shows while it is still trying, and what it shows once trying has gone on too long.
 *
 * A spinner is an honest thing to show for a second and a lie after ten: it says "this is
 * progressing" when what is happening is a request nobody is going to answer. That is I-0173, and
 * ADR-050 makes it likelier rather than rarer, because the address is now something a person chose
 * and can get wrong. So waiting is given words, the address is named — a reader cannot check an
 * address they were never shown — and the wait has a documented end after which the screen says
 * plainly that it is reaching nothing and offers the two repairs: try again, or point somewhere
 * else.
 */

export interface IDeploymentWaitScreenProps {
  /** A failure already known; when set, the screen stops being patient immediately. */
  failure?: HeldSentenceType | null;
  /** The wait has outlived its budget, so silence is now the news. */
  overdue: boolean;
  onRetry: () => void;
  address?: string;
}

/** Long enough for a slow tenant, short enough that nobody sits watching a spinner think. */
export const DEPLOYMENT_WAIT_OVERDUE_MS = 6_000;
/**
 * Below this, saying anything at all would be a flicker rather than information.
 *
 * I-1206: the launch budget, 2.0 s to interactive, not less. At 700 ms a phone's ordinary cold
 * launch crossed it: the session's answer came about 860 ms after the gate mounted, so every launch
 * of the app flashed "Reconnecting your session" for the last 160 ms of a healthy boot. A check
 * still unanswered past the budget is news; one inside it is the boot itself.
 */
export const DEPLOYMENT_WAIT_QUIET_MS = 2_000;

/**
 * F-0578 WAIT-8: the words sit under the launch study's reach, so the study does not move when they
 * come. The launch unit is 13% of the short side, at most 80 px (`WAITING_MOTION`), and the widest
 * study reaches 2.05 units from the centre (Assemble's fly-in).
 */
const CAPTION_TOP = `calc(50% + min(${WAITING_MOTION.launchUnit * 100}vmin, ${WAITING_MOTION.launchUnitMaxPx}px) * 2.1)`;

export function DeploymentWaitScreen({
  failure,
  overdue,
  onRetry,
  address,
}: IDeploymentWaitScreenProps) {
  const t = useT();
  /*
   * T-0320, F-0237: inside the iPhone and iPad app the deployment is not shown by its address, and
   * another deployment arrives only by a setup link or file, so there is no way out to `/connect`.
   */
  const inShell = isShellRuntime();
  const label = inShell
    ? 'FEATit'
    : deploymentAddressLabel(address ?? effectiveDeploymentAddress());
  const stalled = overdue || Boolean(failure);

  if (!stalled) {
    /*
     * F-0578 WAIT-1, WAIT-8 (I-1688): still waiting, so still the launch. The launch's study stays
     * where it was drawn — the window's centre, at launch size, on the session's clock — and the
     * words come in under it: what is being checked, and the way to try again. No FEATit mark: the
     * mark is never a sign of waiting. It returns, in `AuthFrame`'s header, once the screen stops
     * waiting and says what went wrong.
     */
    return (
      <div data-deployment-wait="" className="fixed inset-0">
        <div className="absolute inset-0 flex items-center justify-center">
          {/* WAIT-18: inside the app the shell's cover is the one launch study. */}
          {!inShell && <Waiting size="launch" silent />}
        </div>
        <div
          className="absolute inset-x-0 flex flex-col items-center px-4 text-center"
          style={{ top: CAPTION_TOP }}
        >
          <h1 className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {t('onboarding.wait.title')}
          </h1>
          <p
            aria-live="polite"
            className="mt-1 max-w-[320px] text-[13px] leading-5 text-gray-500 dark:text-gray-400"
          >
            {t('onboarding.wait.checking', { label })}
          </p>
          <Button type="button" size="md" className="mt-4" onClick={onRetry}>
            <RefreshCw className="h-3.5 w-3.5" />
            {t('onboarding.common.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <AuthFrame
      title={
        inShell ? t('onboarding.wait.couldNotReach') : t('onboarding.wait.cannotReach', { label })
      }
    >
      <div className={`${AUTH_CARD_CLASS} text-center`}>
        <CloudOff className="mx-auto h-7 w-7 text-amber-600 dark:text-amber-400" />
        <p
          aria-live="polite"
          className="mt-3 text-[13px] leading-5 text-gray-500 dark:text-gray-400"
        >
          {failure ? sayFailure(t, failure) : t('onboarding.wait.notAnswered')}
        </p>
        <div className="mt-5 flex flex-col gap-3">
          {/*
           * Primary, not secondary: retrying is the action this screen exists for, and the shared
           * secondary style in dark mode is nearly the card's own colour — a button that has to be
           * hunted for on the screen somebody lands on when nothing works.
           */}
          <Button type="button" size="md" onClick={onRetry}>
            <RefreshCw className="h-3.5 w-3.5" />
            {t('onboarding.common.tryAgain')}
          </Button>
          {!inShell && (
            <Link
              to="/connect"
              className="rounded text-[13px] font-medium evx-link transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            >
              {t('onboarding.wait.other')}
            </Link>
          )}
        </div>
      </div>
    </AuthFrame>
  );
}
